import { useCallback, useEffect, useRef, useState } from 'react';

import {
  getWhatsappPasskeyChallenge,
  submitWhatsappPasskeyAssertion,
  type TWhatsappPasskeyAssertion,
} from '@/shared/api/channels/whatsappPasskey';
import { pingPasskeyBridge, signPasskeyChallenge } from '@/shared/lib/passkeyBridge';

export type TPasskeyPhase =
  | 'idle'
  | 'awaiting_challenge'
  | 'extension_missing'
  | 'ready_to_sign'
  | 'signing'
  | 'submitted'
  | 'error';

const CHALLENGE_POLL_MS = 3_000;

/**
 * Drives the WhatsApp passkey (Shortcake) step while the QR-auth dialog is open:
 * polls the ceremony challenge, detects the browser bridge and submits the
 * signed assertion. The dialog stays open afterwards — the channel status
 * polling (useQrCodeByChannel) reacts to `authorized`.
 */
export const useWhatsappPasskey = (channelId: string, enabled: boolean) => {
  const [phase, setPhase] = useState<TPasskeyPhase>('idle');
  const [error, setError] = useState<string | null>(null);
  const challengeRef = useRef<string | null>(null);
  const disposedRef = useRef(false);
  const submittingRef = useRef(false);
  const phaseRef = useRef<TPasskeyPhase>(phase);
  phaseRef.current = phase;

  const confirm = useCallback(async () => {
    const requestOptions = challengeRef.current;
    if (!requestOptions || submittingRef.current) return;
    submittingRef.current = true;
    setPhase('signing');
    setError(null);
    try {
      const assertion = await signPasskeyChallenge(channelId, JSON.parse(requestOptions));
      if (!assertion.response?.clientDataJSON || !assertion.response?.authenticatorData || !assertion.response?.signature) {
        throw new Error('passkey_sign_failed');
      }
      await submitWhatsappPasskeyAssertion(channelId, assertion as TWhatsappPasskeyAssertion);
      setPhase('submitted');
    } catch (err) {
      setPhase('error');
      setError(err instanceof Error ? err.message : 'passkey_error');
    } finally {
      submittingRef.current = false;
    }
  }, [channelId]);

  useEffect(() => {
    if (!enabled) {
      setPhase('idle');
      return;
    }
    disposedRef.current = false;
    let timer: ReturnType<typeof setTimeout>;

    const poll = async () => {
      try {
        const challenge = await getWhatsappPasskeyChallenge(channelId);
        if (disposedRef.current) return;
        if (challenge.status === 'pending' && challenge.requestOptions) {
          challengeRef.current = challenge.requestOptions;
          if (!submittingRef.current && phaseRef.current !== 'submitted' && phaseRef.current !== 'signing') {
            const bridgeReady = await pingPasskeyBridge();
            if (disposedRef.current) return;
            setPhase(bridgeReady ? 'ready_to_sign' : 'extension_missing');
          }
        } else if (phaseRef.current === 'awaiting_challenge' || phaseRef.current === 'idle') {
          setPhase('awaiting_challenge');
        }
      } catch {
        // challenge endpoint failures are non-fatal: the ceremony may not exist yet
      }
      if (!disposedRef.current) timer = setTimeout(poll, CHALLENGE_POLL_MS);
    };

    void poll();
    return () => {
      disposedRef.current = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelId, enabled]);

  return { phase, error, confirm };
};
